import { useEffect, useMemo, useState } from 'react';

export interface PaymentDeadline {
    expiresAt?: string | null;
    assignedAt?: string;
    durationSeconds?: number;
    serverTime?: string;
}

export function usesLocalDeadline(policy?: string | null): boolean {
    return policy === 'cascade_10m_v1' || policy === 'local_10m_v1' || policy === 'local_withdraw_24h_v1';
}

export function utcMilliseconds(value?: string | null): number {
    if (!value) return NaN;
    return Date.parse(/(?:Z|[+-]\d\d:\d\d)$/i.test(value) ? value : `${value}Z`);
}

export function deadlineMilliseconds({ expiresAt, assignedAt, durationSeconds }: PaymentDeadline): number {
    if (expiresAt) return utcMilliseconds(expiresAt);
    return utcMilliseconds(assignedAt) + (durationSeconds ?? 0) * 1000;
}

export function usePaymentDeadline(props: PaymentDeadline) {
    const deadline = deadlineMilliseconds(props);
    const anchor = useMemo(() => ({
        server: Number.isFinite(utcMilliseconds(props.serverTime))
            ? utcMilliseconds(props.serverTime) : Date.now(),
        monotonic: performance.now(),
    }), [props.serverTime]);
    const [, tick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => tick(value => value + 1), 250);
        return () => clearInterval(timer);
    }, []);
    const remaining = deadline - (anchor.server + performance.now() - anchor.monotonic);
    return { deadline, remaining: Math.max(0, remaining), expired: Number.isFinite(remaining) && remaining <= 0 };
}
